import type { ReactElement } from 'react';

interface OAuthLinkingData {
  provider: string;
  idToken: string;
  email: string;
}

interface OAuthLinkingModalProps {
  linkingData: OAuthLinkingData;
  linkPassword: string;
  linkError: string;
  linkLoading: boolean;
  onPasswordChange: (value: string) => void;
  onLink: () => void;
  onCancel: () => void;
}

export function OAuthLinkingModal({
  linkingData,
  linkPassword,
  linkError,
  linkLoading,
  onPasswordChange,
  onLink,
  onCancel,
}: OAuthLinkingModalProps): ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4">
      <div className="bg-theme-surface rounded-2xl p-6 border border-theme-border w-full max-w-sm shadow-xl">
        <h3 className="text-lg font-semibold text-white mb-2">Link Your Account</h3>
        <p className="text-sm text-theme-text-secondary mb-4">
          An account with <span className="text-white font-medium">{linkingData.email}</span>{' '}
          already exists. Enter your password to link{' '}
          {linkingData.provider === 'google' ? 'Google' : 'Apple'} sign-in.
        </p>

        {linkError && (
          <div className="mb-3 p-2 bg-theme-danger/20 border border-theme-danger/30 rounded text-theme-danger text-sm">
            {linkError}
          </div>
        )}

        <input
          type="password"
          value={linkPassword}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Enter your password"
          className="w-full px-3 py-2 bg-theme-bg border border-theme-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-theme-primary mb-4"
        />

        <div className="flex gap-2">
          <button
            type="button"
            onClick={onLink}
            disabled={linkLoading || !linkPassword}
            className="flex-1 py-2 bg-theme-primary-fill hover:bg-theme-primary-fill-hover disabled:bg-theme-surface disabled:text-theme-text-tertiary text-theme-on-primary-fill font-medium rounded-lg transition-colors"
          >
            {linkLoading ? 'Linking...' : 'Link Account'}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={linkLoading}
            className="px-4 py-2 bg-theme-bg hover:bg-theme-border text-theme-text-secondary font-medium rounded-lg transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
